import {
  BaseUser,
  BillingInterval,
  CreateDevice,
  DashboardCardFilters,
  DashboardCardKey,
  DashboardPreference,
  DashboardResponse,
  Device,
  Document,
  DocumentAnswer,
  DocumentAnswerItemSchema,
  DocumentAnswerSchema,
  DocumentAnswerSuggestionSchema,
  DocumentSchema,
  DocumentVersion,
  DocumentVersionApprover,
  DocumentVersionApproverStatus,
  DocumentVersionSchema,
  Folder,
  FolderSchema,
  FullUserUiStateSchema,
  IntegrationEnabledResponseSchema,
  McpConnectionActionResponseSchema,
  McpConnectionEnabledResponseSchema,
  McpConnectionResponseSchema,
  OrgScopedBaseUser,
  PatchDashboardCardPreferencesBody,
  Plan,
  SkillDetailSchema,
  SkillMetadataSchema,
  SkillPatchPayload,
  SkillWritePayload,
  StartMcpOAuthResponseSchema,
  UpdatableUiState,
  UploadFileResponseSchema,
  User,
  UserSurveyAnswers,
  UserSurveyAnswersSchema,
  createDeviceResponseSchema,
  deviceSchema,
  documentTemplateSchema,
} from "@models";
import {
  useMutation,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import {
  fillRouteWithSlugs,
  getLatestVersionFromDocumentVersions,
  queryClient,
  removeDocumentVersionById,
  removeDraftDocumentVersions,
} from "@utils";
import { isAxiosError } from "axios";
import orderBy from "lodash/orderBy";
import uniqueId from "lodash/uniqueId";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "src/components/Snackbar";
import type { McpConnectionPresetKey } from "src/config/mcp-presets";
import { ROUTE_SLUGS } from "src/config/navigation/routes";
import { useStore } from "src/stores";
import {
  getDocGenerationPatchFields,
  getDocGenerationState,
  isDocGenerationRelevantPatch,
  logDocGenerationInfo,
  logDocGenerationWarn,
} from "src/utils/documentGenerationDebug";
import { removeSpecialCharactersFomFilename } from "src/utils/files";
import { object } from "yup";
import {
  DYNAMIC_CONTENT_QUERY_KEY_SEGMENT,
  ROUTES,
  baseDocumentQueryKey,
  baseDocumentTrashQueryKey,
  baseDocumentsQueryKey,
  baseUserTodosQueryKey,
  baseUserTodosSummaryQueryKey,
  getAllFilesInOrgQueryKey,
  getAnswerItemsQueryKey,
  getApproversQueryKey,
  getCreateDocumentMutationKey,
  getDashboardQueryKey,
  getDeviceQueryKey,
  getDevicesQueryKey,
  getDocumentQueryKey,
  getDocumentTemplatesQueryKey,
  getDocumentsQueryKey,
  getFileQueryKey,
  getFoldersQueryKey,
  getFunctionalGroupsQueryKey,
  getIntegrationQueryKey,
  getMcpConnectionsQueryKey,
  getOrgTasksQueryKey,
  getOrganizationUsersQueryKey,
  getPatchApproverStatusMutationKey,
  getQualityEventDocumentReferencesQueryKey,
  getSlackAuthUrlQueryKey,
  getUserQueryKey,
  getUserSurveyAnswersQueryKey,
  transformTemplateToFormat,
} from "../config";

import { getSkillQueryKey, getUserTodosQueryKey } from "../config/request-keys";
import {
  CreateDocumentRequestBody,
  CreateFunctionalGroupData,
  PatchFunctionalGroupData,
  addFunctionalGroupMember,
  checkout,
  createAuthlessMcpConnection,
  createDocument,
  createDocumentVersion,
  createEcfrPin,
  createFolder,
  createFunctionalGroup,
  createManualMcpConnection,
  createSkill,
  deleteDevice,
  deleteDocument,
  deleteDocumentApprover,
  deleteDocumentDrafts,
  deleteDocumentVersion,
  deleteFile,
  deleteFolder,
  deleteFunctionalGroup,
  deleteSkill,
  deleteTemplate,
  deleteUserFromOrg,
  disconnectIntegration,
  disconnectMcpConnection,
  duplicateSkill,
  emptyDocumentTrash,
  parseWebsiteToDevice,
  patchApproverStatus,
  patchCustomTemplate,
  patchDashboardCardPreferences,
  patchDevice,
  patchDocument,
  patchDocumentAnswer,
  patchDocumentAnswerItem,
  patchDocumentVersion,
  patchDocumentVersionDataKeys,
  patchFolder,
  patchFunctionalGroup,
  patchSkill,
  patchUserInOrg,
  patchUserMe,
  patchUserSurveyAnswers,
  permanentlyDeleteDocument,
  postBillingSession,
  postCustomTemplate,
  postDemoDevice,
  postDevice,
  postDocumentAnswer,
  postDocumentAnswerItem,
  postDocumentAnswerSuggestion,
  postDocumentApprover,
  postUserInvite,
  reactivateUserInOrg,
  removeFunctionalGroupMember,
  replaceDocumentApprovers,
  restoreDocument,
  sendPinResetEmail,
  sendSlackTest,
  setIntegrationEnabled,
  setMcpConnectionEnabled,
  setSkillEnabled,
  startMcpOAuthConnection,
  testMcpConnection,
  updateEcfrPin,
  updateOrg,
  updateUserUiState,
  uploadFile,
  validateEcfrPin,
} from "../services";
import {
  AnswerItem,
  CreateDemoDeviceRequest,
  CreateDeviceUiState,
  Fields,
  IntegrationType,
  PatchDocumentVersionData,
  RegulatoryType,
  TemplateConfig,
  TemplateQuestion,
} from "../types";
import { invalidateCachedDashboardQueries } from "./queries/dashboard-invalidation";
import { invalidateCachedSkillQueries } from "./queries/skills";

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === "string" && data.trim()) {
      return data;
    }
    if (
      typeof data === "object" &&
      data !== null &&
      "message" in data &&
      typeof data.message === "string" &&
      data.message.trim()
    ) {
      return data.message;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

const getTaskListAndSummaryInvalidations = (
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string
) =>
  [
    queryClient.invalidateQueries({
      queryKey: getOrgTasksQueryKey(orgId, "all"),
    }),
    queryClient.invalidateQueries({
      queryKey: getOrgTasksQueryKey(orgId, "mine"),
    }),
    queryClient.invalidateQueries({
      queryKey: baseUserTodosQueryKey,
    }),
    queryClient.invalidateQueries({
      queryKey: baseUserTodosSummaryQueryKey,
    }),
    invalidateCachedDashboardQueries(queryClient),
  ].filter(Boolean);

const invalidateDocumentLists = (
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string
) => {
  [
    baseDocumentsQueryKey,
    baseDocumentTrashQueryKey,
    getQualityEventDocumentReferencesQueryKey(orgId),
  ].forEach((queryKey) =>
    queryClient.invalidateQueries({ queryKey, cancelRefetch: false })
  );
};

/**
 * Invalidate EVERY dynamic-content query in the cache. Tier-2 data-key resolution
 * is cross-document (an answer feeds other documents within the same device scope,
 * and org-level answers feed device documents), so editing an answer in one
 * document can change another document's resolved value — we can't scope this to a
 * single doc/version. Matches any query whose key contains the shared
 * `dynamic-content` segment.
 */
const invalidateDynamicContent = (
  queryClient: ReturnType<typeof useQueryClient>
) =>
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey.includes(DYNAMIC_CONTENT_QUERY_KEY_SEGMENT),
  });

const getSkillInvalidations = (
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string,
  skillId?: string
) =>
  [
    skillId &&
      queryClient.invalidateQueries({
        queryKey: getSkillQueryKey(orgId, skillId),
      }),
    invalidateCachedSkillQueries(queryClient, orgId),
  ].filter(Boolean);

const isApproverForUser = (approver: DocumentVersionApprover, userId: string) =>
  approver.userId === userId || approver.user.id === userId;

const patchApproverStatusInApprovers = (
  approvers: DocumentVersionApprover[],
  userId: string,
  status: DocumentVersionApproverStatus
) => {
  const updatedAt = new Date();

  return approvers.map((approver) =>
    isApproverForUser(approver, userId)
      ? { ...approver, approvalStatus: status, updatedAt }
      : approver
  );
};

const patchApproverStatusInDocument = ({
  document,
  documentId,
  versionId,
  userId,
  status,
}: {
  document: Document;
  documentId: string;
  versionId: string;
  userId: string;
  status: DocumentVersionApproverStatus;
}) => {
  if (document.id !== documentId) {
    return document;
  }

  return {
    ...document,
    versions: document.versions.map((version) =>
      version.id === versionId
        ? {
            ...version,
            documentVersionApprover: patchApproverStatusInApprovers(
              version.documentVersionApprover,
              userId,
              status
            ),
          }
        : version
    ),
  };
};

const getPatchedDashboardFilters = (
  currentFilters: DashboardCardFilters | undefined,
  patchFilters: PatchDashboardCardPreferencesBody["filters"]
): DashboardCardFilters => {
  const nextFilters: DashboardCardFilters = { ...(currentFilters ?? {}) };

  Object.entries(patchFilters).forEach(([filterKey, value]) => {
    const key = filterKey as keyof DashboardCardFilters;
    if (value === null) {
      delete nextFilters[key];
      return;
    }

    nextFilters[key] = value;
  });

  return nextFilters;
};

const getPatchedDashboardPreference = (
  cardKey: DashboardCardKey,
  currentPreference: DashboardPreference | undefined,
  patchFilters: PatchDashboardCardPreferencesBody["filters"]
): DashboardPreference => ({
  cardKey,
  filters: getPatchedDashboardFilters(currentPreference?.filters, patchFilters),
});

export const usePatchDashboardCardPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      cardKey,
      deviceId,
      filters,
    }: {
      orgId: string;
      cardKey: DashboardCardKey;
      deviceId?: string;
      filters: PatchDashboardCardPreferencesBody["filters"];
    }) => {
      const { data } = await patchDashboardCardPreferences(orgId, cardKey, {
        deviceId: deviceId ?? null,
        filters,
      });
      return data;
    },
    onMutate: async ({ orgId, cardKey, deviceId, filters }) => {
      const dashboardQueryKey = getDashboardQueryKey(orgId, deviceId);

      await queryClient.cancelQueries({ queryKey: dashboardQueryKey });

      const previousDashboard =
        queryClient.getQueryData<DashboardResponse>(dashboardQueryKey);

      queryClient.setQueryData<DashboardResponse>(
        dashboardQueryKey,
        (current) => {
          if (!current) {
            return current;
          }

          const nextPreference = getPatchedDashboardPreference(
            cardKey,
            current.preferences[cardKey],
            filters
          );

          return {
            ...current,
            preferences: {
              ...current.preferences,
              [cardKey]: nextPreference,
            },
            cards: current.cards.map((card) =>
              card.key === cardKey
                ? { ...card, filters: nextPreference.filters }
                : card
            ),
          };
        }
      );

      return {
        dashboardQueryKey,
        previousDashboard,
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousDashboard) {
        queryClient.setQueryData(
          context.dashboardQueryKey,
          context.previousDashboard
        );
      }
    },
    onSettled: (_data, _error, { orgId, deviceId }) => {
      queryClient.invalidateQueries({
        queryKey: getDashboardQueryKey(orgId, deviceId),
      });
    },
  });
};

export const useCreateSkillMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      data,
    }: {
      orgId: string;
      data: SkillWritePayload;
    }) =>
      await SkillMetadataSchema.validate(
        (await createSkill({ orgId, data })).data
      ),
    onSuccess: (_, { orgId }) => {
      getSkillInvalidations(queryClient, orgId);
    },
  });
};

export const usePatchSkillMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      skillId,
      data,
    }: {
      orgId: string;
      skillId: string;
      data: SkillPatchPayload;
    }) =>
      await SkillDetailSchema.validate(
        (await patchSkill({ orgId, skillId, data })).data
      ),
    onSuccess: (_, { orgId, skillId }) => {
      getSkillInvalidations(queryClient, orgId, skillId);
    },
  });
};

export const useSetSkillEnabledMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      skillId,
      enabled,
    }: {
      orgId: string;
      skillId: string;
      enabled: boolean;
    }) =>
      await SkillDetailSchema.validate(
        (await setSkillEnabled({ orgId, skillId, enabled })).data
      ),
    onSuccess: (_, { orgId, skillId }) => {
      getSkillInvalidations(queryClient, orgId, skillId);
    },
  });
};

export const useDuplicateSkillMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      skillId,
      data,
    }: {
      orgId: string;
      skillId: string;
      data?: Partial<SkillWritePayload>;
    }) =>
      await SkillMetadataSchema.validate(
        (await duplicateSkill({ orgId, skillId, data })).data
      ),
    onSuccess: (_, { orgId }) => {
      getSkillInvalidations(queryClient, orgId);
    },
  });
};

export const useDeleteSkillMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSkill,
    onSuccess: (_, { orgId, skillId }) => {
      getSkillInvalidations(queryClient, orgId, skillId);
    },
  });
};

type DeleteDocumentVersionContext = {
  previousDocument: Document | undefined;
  previousDocuments: Document[] | undefined;
  hasRemainingVersionsAfterDelete: boolean | undefined;
};

type DeleteVersionMutationVariables = {
  orgId: string;
  deviceId: string;
  docId: string;
};

const getDocumentListAfterVersionDelete = ({
  documents,
  docId,
  previousDocument,
  nextDocumentVersions,
  getRemainingVersions,
}: {
  documents: Document[];
  docId: string;
  previousDocument: Document | undefined;
  nextDocumentVersions: DocumentVersion[] | undefined;
  getRemainingVersions: (versions: DocumentVersion[]) => DocumentVersion[];
}): Document[] => {
  if (previousDocument) {
    if (!nextDocumentVersions || nextDocumentVersions.length === 0) {
      return documents.filter((document) => document.id !== docId);
    }

    const latestRemainingVersion =
      getLatestVersionFromDocumentVersions(nextDocumentVersions);

    if (!latestRemainingVersion) {
      return documents.filter((document) => document.id !== docId);
    }

    return documents.map((document) =>
      document.id === docId
        ? {
            ...previousDocument,
            versions: [latestRemainingVersion],
          }
        : document
    );
  }

  return documents
    .map((document) =>
      document.id === docId
        ? {
            ...document,
            versions: getRemainingVersions(document.versions),
          }
        : document
    )
    .filter((document) => document.versions.length > 0);
};

const applyOptimisticDeleteVersionUpdate = ({
  queryClient,
  deviceId,
  docId,
  getRemainingVersions,
}: {
  queryClient: ReturnType<typeof useQueryClient>;
  deviceId: string;
  docId: string;
  getRemainingVersions: (versions: DocumentVersion[]) => DocumentVersion[];
}): DeleteDocumentVersionContext => {
  const previousDocument: Document | undefined = queryClient.getQueryData(
    getDocumentQueryKey(docId)
  );

  const nextDocumentVersions = previousDocument
    ? getRemainingVersions(previousDocument.versions)
    : undefined;

  const hasRemainingVersionsAfterDelete = nextDocumentVersions
    ? nextDocumentVersions.length > 0
    : undefined;

  if (previousDocument && nextDocumentVersions) {
    queryClient.setQueryData(getDocumentQueryKey(docId), {
      ...previousDocument,
      versions: nextDocumentVersions,
    });
  }

  const previousDocuments: Document[] | undefined = queryClient.getQueryData(
    getDocumentsQueryKey(deviceId)
  );

  if (previousDocuments) {
    const nextDocuments = getDocumentListAfterVersionDelete({
      documents: previousDocuments,
      docId,
      previousDocument,
      nextDocumentVersions,
      getRemainingVersions,
    });

    queryClient.setQueryData(getDocumentsQueryKey(deviceId), nextDocuments);
  }

  return {
    previousDocument,
    previousDocuments,
    hasRemainingVersionsAfterDelete,
  };
};

const getShouldKeepDocumentQuery = ({
  hasRemainingVersionsAfterDelete,
  remainingVersionsInDocumentsCache,
}: {
  hasRemainingVersionsAfterDelete: boolean | undefined;
  remainingVersionsInDocumentsCache: number | undefined;
}) => {
  if (hasRemainingVersionsAfterDelete !== undefined) {
    return hasRemainingVersionsAfterDelete;
  }

  if (remainingVersionsInDocumentsCache !== undefined) {
    return remainingVersionsInDocumentsCache > 0;
  }

  return true;
};

const handleDeleteVersionMutationSuccess = ({
  queryClient,
  orgId,
  deviceId,
  docId,
  context,
}: DeleteVersionMutationVariables & {
  queryClient: ReturnType<typeof useQueryClient>;
  context: DeleteDocumentVersionContext | undefined;
}) => {
  const remainingVersionsInDocumentsCache = queryClient
    .getQueryData<Document[]>(getDocumentsQueryKey(deviceId))
    ?.find((document) => document.id === docId)?.versions.length;

  const shouldKeepDocumentQuery = getShouldKeepDocumentQuery({
    hasRemainingVersionsAfterDelete: context?.hasRemainingVersionsAfterDelete,
    remainingVersionsInDocumentsCache,
  });

  if (shouldKeepDocumentQuery) {
    queryClient.invalidateQueries({
      queryKey: getDocumentQueryKey(docId),
    });
  } else {
    queryClient.removeQueries({
      queryKey: getDocumentQueryKey(docId),
      exact: true,
    });
  }

  queryClient.invalidateQueries({
    queryKey: getDocumentsQueryKey(deviceId),
    cancelRefetch: false,
  });

  queryClient.invalidateQueries({
    queryKey: getDocumentTemplatesQueryKey(orgId, deviceId),
    cancelRefetch: false,
  });

  queryClient.invalidateQueries({
    queryKey: getQualityEventDocumentReferencesQueryKey(orgId),
  });

  getTaskListAndSummaryInvalidations(queryClient, orgId);
};

export const useSaveSuggestion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      suggestion,
      documentVersion,
      step,
      docId,
      threadId,
    }: {
      orgId: string;
      suggestion: string;
      documentVersion: DocumentVersion;
      docId: string;
      step: TemplateQuestion;
      threadId: string;
    }) => {
      const { data } = await postDocumentAnswerSuggestion(
        orgId,
        docId,
        documentVersion.id,
        {
          suggestion,
          element: step.id,
          threadId,
        }
      );
      return await DocumentAnswerSuggestionSchema.validate(data);
    },
    onSuccess: (_, { docId, orgId }) => {
      queryClient.invalidateQueries({
        queryKey: getDocumentQueryKey(docId),
      });
      getTaskListAndSummaryInvalidations(queryClient, orgId);
    },
  });
};

export const usePatchDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      docId,
      data,
    }: {
      orgId: string;
      docId: string;
      deviceId: string;
      queryDeviceIds?: string[];
      data: {
        deviceId?: string | null;
        regulatoryType?: string;
        functionalGroupId?: string;
        folderId?: string | null;
        title?: string;
        contentTemplate?: string;
      };
    }) => {
      const { data: newDoc } = await patchDocument(orgId, docId, data);
      return DocumentSchema.validateSync(newDoc);
    },
    onMutate: async ({ docId, deviceId, queryDeviceIds, data }) => {
      await queryClient.cancelQueries({
        queryKey: getDocumentQueryKey(docId),
      });

      const previousDoc: Document | undefined = queryClient.getQueryData(
        getDocumentQueryKey(docId)
      );

      const nextQueryDeviceIds = [
        ...new Set([...(queryDeviceIds ?? []), deviceId].filter(Boolean)),
      ];
      const previousDocsByDeviceId = new Map<string, Document[]>();

      await Promise.all(
        nextQueryDeviceIds.map((queryDeviceId) =>
          queryClient.cancelQueries({
            queryKey: getDocumentsQueryKey(queryDeviceId),
          })
        )
      );

      nextQueryDeviceIds.forEach((queryDeviceId) => {
        const previousDocs = queryClient.getQueryData<Document[]>(
          getDocumentsQueryKey(queryDeviceId)
        );

        if (previousDocs) {
          previousDocsByDeviceId.set(queryDeviceId, previousDocs);
        }
      });

      const sourceDoc =
        previousDoc ??
        Array.from(previousDocsByDeviceId.values())
          .flat()
          .find((document) => document.id === docId);

      if (!sourceDoc) {
        return;
      }

      const updatedDoc = {
        ...sourceDoc,
        ...data,
      };

      queryClient.setQueryData(getDocumentQueryKey(docId), updatedDoc);

      const touchedDocsByDeviceId = new Map<string, Document[]>();

      previousDocsByDeviceId.forEach((previousDocs, queryDeviceId) => {
        const isDocumentInList = previousDocs.some((doc) => doc.id === docId);

        if (!isDocumentInList) {
          return;
        }

        touchedDocsByDeviceId.set(queryDeviceId, previousDocs);
        queryClient.setQueryData(getDocumentsQueryKey(queryDeviceId), () => [
          ...previousDocs.filter((d) => d.id !== docId),
          updatedDoc,
        ]);
      });

      return {
        didSeedDocQuery: !previousDoc,
        previousDoc,
        previousDocsByDeviceId: touchedDocsByDeviceId,
      };
    },
    onError: (_, { docId }, context) => {
      if (context?.previousDoc) {
        queryClient.setQueryData(
          getDocumentQueryKey(docId),
          context.previousDoc
        );
      } else if (context?.didSeedDocQuery) {
        queryClient.removeQueries({
          queryKey: getDocumentQueryKey(docId),
          exact: true,
        });
      }

      context?.previousDocsByDeviceId?.forEach(
        (previousDocs, queryDeviceId) => {
          queryClient.setQueryData(
            getDocumentsQueryKey(queryDeviceId),
            previousDocs
          );
        }
      );
    },
    onSuccess: (_, { docId, orgId }) => {
      queryClient.invalidateQueries({
        queryKey: baseDocumentsQueryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentQueryKey(docId),
      });
      getTaskListAndSummaryInvalidations(queryClient, orgId);
    },
  });
};

export const useCreateFolderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      data,
    }: {
      orgId: string;
      data: {
        name: string;
        parentFolderId: string | null;
      };
    }) => {
      const response = await createFolder({ data, orgId });
      return FolderSchema.validateSync(response.data);
    },
    onSuccess: (folder, { orgId }) => {
      queryClient.setQueryData(
        getFoldersQueryKey(orgId),
        (old: Folder[] = []) => [...old, folder]
      );
      queryClient.invalidateQueries({
        queryKey: getFoldersQueryKey(orgId),
        cancelRefetch: false,
      });
    },
  });
};

export const usePatchFolderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      folderId,
      data,
    }: {
      orgId: string;
      folderId: string;
      data: {
        name?: string;
        parentFolderId?: string | null;
      };
    }) => {
      const response = await patchFolder({ data, folderId, orgId });
      return FolderSchema.validateSync(response.data);
    },
    onMutate: async ({ orgId, folderId, data }) => {
      const previousFolders = queryClient.getQueryData<Folder[]>(
        getFoldersQueryKey(orgId)
      );

      if (previousFolders) {
        queryClient.setQueryData(
          getFoldersQueryKey(orgId),
          previousFolders.map((folder) =>
            folder.id === folderId ? { ...folder, ...data } : folder
          )
        );
      }

      return { previousFolders };
    },
    onError: (_error, { orgId }, context) => {
      if (context?.previousFolders) {
        queryClient.setQueryData(
          getFoldersQueryKey(orgId),
          context.previousFolders
        );
      }
    },
    onSuccess: (folder, { orgId }) => {
      queryClient.setQueryData(
        getFoldersQueryKey(orgId),
        (old: Folder[] = []) =>
          old.map((item) => (item.id === folder.id ? folder : item))
      );
      queryClient.invalidateQueries({
        queryKey: getFoldersQueryKey(orgId),
        cancelRefetch: false,
      });
    },
  });
};

export const useDeleteFolderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      folderId,
    }: {
      orgId: string;
      folderId: string;
    }) => {
      await deleteFolder({ folderId, orgId });
      return { folderId };
    },
    onMutate: async ({ orgId, folderId }) => {
      const previousFolders = queryClient.getQueryData<Folder[]>(
        getFoldersQueryKey(orgId)
      );
      const deletedFolder = previousFolders?.find(
        (folder) => folder.id === folderId
      );

      if (previousFolders && deletedFolder) {
        queryClient.setQueryData(
          getFoldersQueryKey(orgId),
          previousFolders
            .filter((folder) => folder.id !== folderId)
            .map((folder) =>
              folder.parentFolderId === folderId
                ? { ...folder, parentFolderId: deletedFolder.parentFolderId }
                : folder
            )
        );
      }

      const previousDocumentLists = queryClient.getQueriesData<Document[]>({
        queryKey: baseDocumentsQueryKey,
      });

      previousDocumentLists.forEach(([queryKey, documents]) => {
        if (!documents) {
          return;
        }

        queryClient.setQueryData(
          queryKey,
          documents.map((document) =>
            document.folderId === folderId
              ? { ...document, folderId: deletedFolder?.parentFolderId ?? null }
              : document
          )
        );
      });

      return { previousDocumentLists, previousFolders };
    },
    onError: (_error, { orgId }, context) => {
      if (context?.previousFolders) {
        queryClient.setQueryData(
          getFoldersQueryKey(orgId),
          context.previousFolders
        );
      }

      context?.previousDocumentLists?.forEach(([queryKey, documents]) => {
        queryClient.setQueryData(queryKey, documents);
      });
    },
    onSuccess: (_result, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: getFoldersQueryKey(orgId),
        cancelRefetch: false,
      });
      queryClient.invalidateQueries({
        queryKey: baseDocumentsQueryKey,
        cancelRefetch: false,
      });
    },
  });
};

export const usePatchDocumentVersionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      docId,
      docVersionId,
      data,
    }: {
      orgId: string;
      docId: string;
      deviceId: string;
      docVersionId: string;
      data: PatchDocumentVersionData;
    }) => {
      const { data: newDoc } = await patchDocumentVersion(
        orgId,
        docId,
        docVersionId,
        data
      );
      return DocumentVersionSchema.validateSync(newDoc);
    },
    onMutate: async ({ docId, docVersionId, deviceId, data }) => {
      const previousDoc: Document | undefined = queryClient.getQueryData(
        getDocumentQueryKey(docId)
      );
      const previousDocs: Document[] | undefined = queryClient.getQueryData(
        getDocumentsQueryKey(deviceId)
      );
      const previousVersion = previousDoc?.versions.find(
        (version) => version.id === docVersionId
      );

      // Skip optimistic update when submitting for approval (readyForApproval: true)
      // because the backend bakes snapshot and approval data we cannot synthesize.
      if (data.readyForApproval) {
        if (isDocGenerationRelevantPatch(data)) {
          logDocGenerationInfo("patch-document-version.on-mutate", {
            ...getDocGenerationState({
              docId,
              version: previousVersion,
              versionId: docVersionId,
            }),
            ...getDocGenerationPatchFields(data),
            deviceId,
            optimisticCacheSeeded: false,
          });
        }

        return {
          previousDoc,
          previousDocs,
          optimisticCacheSeeded: false,
        };
      }

      if (!previousDoc || !previousDocs) {
        if (isDocGenerationRelevantPatch(data)) {
          logDocGenerationInfo("patch-document-version.on-mutate", {
            ...getDocGenerationState({
              docId,
              versionId: docVersionId,
            }),
            ...getDocGenerationPatchFields(data),
            deviceId,
            optimisticCacheSeeded: false,
          });
        }

        return {
          optimisticCacheSeeded: false,
        };
      }

      const documentVersion = previousDoc.versions.find(
        (v) => v.id === docVersionId
      );
      if (!documentVersion) {
        if (isDocGenerationRelevantPatch(data)) {
          logDocGenerationInfo("patch-document-version.on-mutate", {
            ...getDocGenerationState({
              docId,
              versionId: docVersionId,
            }),
            ...getDocGenerationPatchFields(data),
            deviceId,
            optimisticCacheSeeded: false,
          });
        }

        return {
          optimisticCacheSeeded: false,
        };
      }

      // When reverting to draft (readyForApproval: false), clear the snapshot
      const optimisticData =
        data.readyForApproval === false ? { ...data, snapshot: null } : data;
      const updatedVersion = { ...documentVersion, ...optimisticData };

      const updatedDoc = {
        ...previousDoc,
        versions: [
          updatedVersion,
          ...previousDoc.versions.filter((v) => v.id !== docVersionId),
        ],
      };

      queryClient.setQueryData(getDocumentQueryKey(docId), updatedDoc);

      if (previousDocs.some((doc) => doc.id === docId)) {
        queryClient.setQueryData(getDocumentsQueryKey(deviceId), [
          ...previousDocs.filter((d) => d.id !== docId),
          updatedDoc,
        ]);
      }

      if (isDocGenerationRelevantPatch(data)) {
        logDocGenerationInfo("patch-document-version.on-mutate", {
          ...getDocGenerationState({
            docId,
            version: updatedVersion,
          }),
          ...getDocGenerationPatchFields(data),
          deviceId,
          optimisticCacheSeeded: true,
        });
      }

      return { previousDoc, previousDocs, optimisticCacheSeeded: true };
    },
    onError: (error, { docId, docVersionId, deviceId, data }, context) => {
      if (isDocGenerationRelevantPatch(data)) {
        const previousVersion = context?.previousDoc?.versions.find(
          (version) => version.id === docVersionId
        );

        logDocGenerationWarn("patch-document-version.on-error", {
          ...getDocGenerationState({
            docId,
            version: previousVersion,
            versionId: docVersionId,
          }),
          ...getDocGenerationPatchFields(data),
          deviceId,
          optimisticCacheSeeded: context?.optimisticCacheSeeded === true,
          errorMessage: error instanceof Error ? error.message : String(error),
        });
      }

      if (
        context?.optimisticCacheSeeded &&
        context.previousDoc &&
        context.previousDocs
      ) {
        queryClient.setQueryData(
          getDocumentQueryKey(context.previousDoc.id),
          context.previousDoc
        );
        queryClient.setQueryData(
          getDocumentsQueryKey(context.previousDoc.deviceId || ""),
          context.previousDocs
        );
      }
    },
    onSuccess: (
      updatedVersion,
      { deviceId, docId, docVersionId, orgId, data }
    ) => {
      if (isDocGenerationRelevantPatch(data)) {
        logDocGenerationInfo("patch-document-version.on-success", {
          ...getDocGenerationState({
            docId,
            version: updatedVersion,
            versionId: docVersionId,
          }),
          ...getDocGenerationPatchFields(data),
          deviceId,
        });
      }

      const currentDocFromAllDocs = (
        queryClient.getQueryData(getDocumentsQueryKey(deviceId)) as
          | Document[]
          | undefined
      )?.find((doc) => doc.id === docId);

      const replaceVersion = (versions: DocumentVersion[]) =>
        orderBy(
          versions.map((version) =>
            version.id === updatedVersion.id ? updatedVersion : version
          ),
          "createdAt",
          "desc"
        );

      queryClient.setQueryData(
        getDocumentQueryKey(docId),
        (old: Document | undefined) => {
          const previousData = old || currentDocFromAllDocs;

          if (
            !previousData ||
            !previousData.versions.some(
              (version) => version.id === updatedVersion.id
            )
          ) {
            return old;
          }

          return {
            ...previousData,
            versions: replaceVersion(previousData.versions),
          };
        }
      );

      queryClient.setQueryData(
        getDocumentsQueryKey(deviceId),
        (old: Document[] | undefined) =>
          old?.map((doc) => {
            if (
              doc.id !== docId ||
              !doc.versions.some((version) => version.id === updatedVersion.id)
            ) {
              return doc;
            }

            return {
              ...doc,
              versions: replaceVersion(doc.versions),
            };
          })
      );
      if (data.readyForApproval === false) {
        queryClient.setQueryData(
          getApproversQueryKey(docId, docVersionId),
          updatedVersion.documentVersionApprover
        );
        queryClient.invalidateQueries({
          queryKey: getApproversQueryKey(docId, docVersionId),
        });
      }
      getTaskListAndSummaryInvalidations(queryClient, orgId);
    },
  });
};

export const usePatchDocumentAnswerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      docId,
      versionId,
      element,
      answer,
    }: {
      orgId: string;
      docId: string;
      deviceId: string;
      versionId: string;
      element: string;
      answer: string | null;
    }) => {
      const { data } = await patchDocumentAnswer({
        orgId,
        docId,
        versionId,
        data: { element, answer },
      });
      return DocumentAnswerSchema.validateSync(data);
    },
    onSuccess: (_, { deviceId, docId }) => {
      queryClient.invalidateQueries({
        queryKey: getDocumentsQueryKey(deviceId),
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentQueryKey(docId),
      });
      invalidateDynamicContent(queryClient);
      invalidateCachedDashboardQueries(queryClient);
    },
  });
};

export const useCreateDemoDeviceMutation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async ({
      request,
      orgId,
    }: {
      request: CreateDemoDeviceRequest;
      orgId: string;
    }) => {
      const { data } = await postDemoDevice(orgId, request);
      return await deviceSchema.validate(data);
    },
    onSuccess: (data, { orgId }) => {
      queryClient.setQueryData(getDevicesQueryKey(orgId), (old: Device[]) => [
        ...old,
        data,
      ]);
      navigate(
        fillRouteWithSlugs(ROUTES.DEVICE_OVERVIEW, {
          [ROUTE_SLUGS.DEVICE_ID]: data.id,
          [ROUTE_SLUGS.ORG_ID]: orgId,
        })
      );
    },
  });
};

export const useUpdateUserSurveyAnswersMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      data,
    }: {
      data: Partial<UserSurveyAnswers>;
      userId: string;
    }) => {
      const { data: updatedData } = await patchUserSurveyAnswers(data);
      return await UserSurveyAnswersSchema.validate(updatedData);
    },
    onMutate: (data: { data: Partial<UserSurveyAnswers>; userId: string }) => {
      queryClient.cancelQueries({
        queryKey: getUserSurveyAnswersQueryKey(data.userId),
      });

      const previousData = queryClient.getQueryData<UserSurveyAnswers>(
        getUserSurveyAnswersQueryKey(data.userId)
      );

      queryClient.setQueryData(
        getUserSurveyAnswersQueryKey(data.userId),
        (old: UserSurveyAnswers | undefined) => ({
          ...old,
          ...data.data,
        })
      );

      return { previousData, userId: data.userId };
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          getUserSurveyAnswersQueryKey(context.userId),
          context.previousData
        );
      }
    },
  });
};

export const useUpdateDeviceMutation = ({
  orgId,
  deviceId,
}: {
  orgId: string;
  deviceId: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ data }: { data: Partial<CreateDeviceUiState> }) => {
      const { data: updatedData } = await patchDevice({
        orgId,
        deviceId,
        data,
      });
      return await deviceSchema.validate(updatedData);
    },
    onMutate: ({ data }) => {
      const previousData = queryClient.getQueryData<Device>(
        getDeviceQueryKey(deviceId)
      );

      queryClient.setQueryData(
        getDeviceQueryKey(deviceId),
        (old: Device | undefined) => {
          if (!old) return undefined;
          return {
            ...old,
            ...data,
            ...(data.components && {
              components: data.components.map((newComponent, index) => {
                const oldComponent = old.components[index];
                return {
                  ...oldComponent,
                  ...newComponent,
                  configuration: newComponent.configuration || [],
                  subComponents:
                    newComponent.subComponents?.map(
                      (newSubComponent, subIndex) => {
                        const oldSubComponent =
                          oldComponent?.subComponents?.[subIndex];
                        return {
                          ...oldSubComponent,
                          ...newSubComponent,
                          configuration: newSubComponent.configuration || [],
                        };
                      }
                    ) || [],
                };
              }),
            }),
          };
        }
      );

      return { previousData, deviceId };
    },
    onSuccess: (_) => {
      queryClient.invalidateQueries({
        queryKey: getDevicesQueryKey(orgId),
      });
      queryClient.invalidateQueries({
        queryKey: getDeviceQueryKey(deviceId),
      });
      invalidateCachedDashboardQueries(queryClient);
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          getDeviceQueryKey(context.deviceId),
          context.previousData
        );
      }
    },
  });
};

export const useCreateDeviceMutation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async ({
      device,
      orgId,
    }: {
      device: CreateDevice;
      orgId: string;
    }) => {
      const { data } = await postDevice(orgId, device);
      return await deviceSchema.validate(data);
    },
    onSuccess: (data, { orgId }) => {
      queryClient.setQueryData(getDevicesQueryKey(orgId), (old: Device[]) => [
        ...old,
        data,
      ]);
      invalidateCachedDashboardQueries(queryClient);
      navigate(
        fillRouteWithSlugs(ROUTES.DEVICE_OVERVIEW, {
          [ROUTE_SLUGS.DEVICE_ID]: data.id,
          [ROUTE_SLUGS.ORG_ID]: orgId,
        })
      );
    },
  });
};

export const useCreateDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: getCreateDocumentMutationKey(),
    mutationFn: async ({
      orgId,
      body,
    }: {
      orgId: string;
      body?: CreateDocumentRequestBody;
    }) => {
      const { data } = await createDocument(orgId, body);
      return DocumentSchema.validate(data);
    },
    onSuccess: async (data, { orgId, body }) => {
      if (body?.deviceId) {
        queryClient.setQueryData(
          getDocumentsQueryKey(body.deviceId),
          (old: Document[] | undefined) => {
            if (!old) {
              return old;
            }

            const existingIndex = old.findIndex((doc) => doc.id === data.id);
            if (existingIndex === -1) {
              return [...old, data];
            }

            return old.map((doc) => (doc.id === data.id ? data : doc));
          }
        );

        queryClient.invalidateQueries({
          queryKey: getDocumentsQueryKey(body.deviceId),
        });
      }

      queryClient.setQueryData(getDocumentQueryKey(data.id), data);
      getTaskListAndSummaryInvalidations(queryClient, orgId);
    },
  });
};

export const useDeleteTemplateMutation = () => {
  return useMutation({
    mutationFn: async ({
      orgId,
      deviceId,
      templateKey,
    }: {
      orgId: string;
      deviceId: string;
      templateKey: string;
    }) => {
      await deleteTemplate(orgId, deviceId, templateKey);
    },
    onMutate: (data: {
      orgId: string;
      deviceId: string;
      templateKey: string;
    }) => {
      const previousTemplates: TemplateConfig | undefined =
        queryClient.getQueryData(
          getDocumentTemplatesQueryKey(data.orgId, data.deviceId)
        );

      queryClient.setQueryData(
        getDocumentTemplatesQueryKey(data.orgId, data.deviceId),
        (old: TemplateConfig | undefined) => {
          if (!old) return old;
          const { [data.templateKey]: deletedTemplate, ...remainingTemplates } =
            old;
          return remainingTemplates;
        }
      );

      return { previousTemplates };
    },
    onSuccess: (_, { orgId, deviceId }) => {
      queryClient.invalidateQueries({
        queryKey: getDocumentTemplatesQueryKey(orgId, deviceId),
        cancelRefetch: false,
      });

      queryClient.invalidateQueries({
        queryKey: getDocumentsQueryKey(deviceId),
      });
    },
  });
};

export const useUploadFileMutation = () => {
  const queryClient = useQueryClient();
  // Todo: Caching the file
  return useMutation({
    mutationFn: async ({
      orgId,
      file,
      deviceId,
    }: {
      orgId: string;
      file: File;
      deviceId?: string;
    }) => {
      const { data } = await uploadFile({
        orgId,
        file: file && removeSpecialCharactersFomFilename(file),
        deviceId,
      });
      return UploadFileResponseSchema.validateSync(data);
    },
    onSuccess: (data, { deviceId }) => {
      queryClient.invalidateQueries({
        queryKey: getAllFilesInOrgQueryKey(deviceId),
      });
      queryClient.invalidateQueries({
        queryKey: getFileQueryKey(data.id),
      });
    },
  });
};

export const useSaveAnswerItemMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      data,
      answerId,
      orgId,
      docId,
      versionId,
    }: {
      orgId: string;
      deviceId: string;
      documentVersion: DocumentVersion;
      docId: string;
      versionId: string;
      answerId: string;
      data: {
        type: string;
        fields: Fields;
      };
      step: TemplateQuestion;
      answerItems: AnswerItem[];
    }) => {
      const { data: updatedAnswer } = await postDocumentAnswerItem({
        orgId,
        docId,
        versionId,
        answerId,
        data,
      });

      return DocumentAnswerItemSchema.validateSync(updatedAnswer);
    },
    onMutate: async ({
      documentVersion,
      step,
      answerItems,
      deviceId,
      docId,
    }) => {
      const previousDoc: Document | undefined = queryClient.getQueryData(
        getDocumentQueryKey(docId)
      );
      const previousAnswer = documentVersion.answers.find(
        (a) => a.element === step.id
      );

      const newAnswer = {
        documentVersionId: documentVersion.id,
        id: previousAnswer?.id || "temp",
        element: step.id,
        answerItems,
        createdBy: previousAnswer?.createdBy || "temp",
        createdAt: previousAnswer?.createdAt || new Date(),
        updatedAt: new Date(),
      };

      const updatedDoc = {
        ...previousDoc,
        versions: previousDoc?.versions.map((version) => {
          if (version.id === documentVersion.id) {
            return {
              ...version,
              answers: [
                ...version.answers.filter(
                  (a: DocumentAnswer) => a.element !== step.id
                ),
                newAnswer,
              ],
            };
          } else {
            return version;
          }
        }),
      };

      queryClient.setQueryData(getDocumentQueryKey(docId), updatedDoc);

      queryClient.setQueryData(
        getDocumentsQueryKey(deviceId),
        (old: Document[]) => [...old.filter((d) => d.id !== docId), updatedDoc]
      );
    },
    onError: (_, variables) => {
      const previousDoc: Document | undefined = queryClient.getQueryData(
        getDocumentQueryKey(variables.docId)
      );

      if (previousDoc) {
        queryClient.setQueryData(
          getDocumentQueryKey(previousDoc.id),
          previousDoc
        );
      }
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getAnswerItemsQueryKey({
          orgId: variables.orgId,
          itemId: data.id,
          type: variables.data.type,
        }),
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentQueryKey(variables.docId),
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentsQueryKey(variables.deviceId),
      });
      invalidateDynamicContent(queryClient);
      getTaskListAndSummaryInvalidations(queryClient, variables.orgId);
    },
  });
};

export const usePatchAnswerItemMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      archived,
      data,
      answerId,
      itemId,
      orgId,
      docId,
      versionId,
    }: {
      orgId: string;
      docId: string;
      versionId: string;
      deviceId: string;
      data: { fields: Fields };
      answerId: string;
      itemId: string;
      type: string;
      step: TemplateQuestion;
      answerItems: AnswerItem[];
      documentVersion: DocumentVersion;
      archived: boolean;
    }) => {
      const { data: updatedDocVersion } = await patchDocumentAnswerItem({
        orgId,
        docId,
        versionId,
        answerId,
        itemId,
        data,
        archived,
      });
      return DocumentAnswerItemSchema.validateSync(updatedDocVersion);
    },
    onMutate: async ({
      documentVersion,
      step,
      answerItems,
      deviceId,
      docId,
    }) => {
      const previousDoc: Document | undefined = queryClient.getQueryData(
        getDocumentQueryKey(docId)
      );
      const previousAnswer = documentVersion.answers.find(
        (a) => a.element === step.id
      );

      const newAnswer = {
        documentVersionId: documentVersion.id,
        id: previousAnswer?.id || "temp",
        element: step.id,
        answerItems: answerItems,
        createdBy: previousAnswer?.createdBy || "temp",
        createdAt: previousAnswer?.createdAt || new Date(),
        updatedAt: new Date(),
        answer: "",
      };

      const updatedDoc = {
        ...previousDoc,
        versions: previousDoc?.versions.map((version) => {
          if (version.id === documentVersion.id) {
            return {
              ...version,
              answers: [
                ...version.answers.filter(
                  (a: DocumentAnswer) => a.element !== step.id
                ),
                newAnswer,
              ],
            };
          } else {
            return version;
          }
        }),
      };

      queryClient.setQueryData(getDocumentQueryKey(docId), updatedDoc);

      queryClient.setQueryData(
        getDocumentsQueryKey(deviceId),
        (old: Document[]) => [...old.filter((d) => d.id !== docId), updatedDoc]
      );
    },
    onError: (_, variables) => {
      const previousDoc: Document | undefined = queryClient.getQueryData(
        getDocumentQueryKey(variables.docId)
      );

      if (previousDoc) {
        queryClient.setQueryData(
          getDocumentQueryKey(previousDoc.id),
          previousDoc
        );

        queryClient.setQueryData(
          getDocumentsQueryKey(variables.deviceId),
          (old: Document[]) => old
        );
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getAnswerItemsQueryKey({
          orgId: variables.orgId,
          itemId: variables.itemId,
          type: variables.type,
        }),
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentQueryKey(variables.docId),
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentsQueryKey(variables.deviceId),
      });
      invalidateDynamicContent(queryClient);
      getTaskListAndSummaryInvalidations(queryClient, variables.orgId);
    },
  });
};
export const useSaveAnswerInCustomDocMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      answer,
      documentVersion,
      dataKey,
    }: {
      orgId: string;
      docId: string;
      deviceId: string;
      documentVersion: DocumentVersion;
      answer?: string | null;
      dataKey: string;
    }) => {
      const updatedDocVersion = await postDocumentAnswer(
        orgId,
        documentVersion.documentId,
        documentVersion.id,
        {
          answer,
          element: dataKey,
        }
      );
      return updatedDocVersion.data;
    },
    onMutate: async ({ docId, deviceId, answer, dataKey, documentVersion }) => {
      // cancel ongoing
      queryClient.cancelQueries({
        queryKey: getDocumentQueryKey(docId),
      });

      queryClient.cancelQueries({
        queryKey: getDocumentsQueryKey(deviceId),
      });

      let previousDocs: Document[] | undefined = queryClient.getQueryData(
        getDocumentsQueryKey(deviceId)
      );

      let previousDoc: Document | undefined =
        queryClient.getQueryData(getDocumentQueryKey(docId)) ||
        previousDocs?.find((d) => d.id === docId);

      if (!previousDoc) {
        return;
      }

      const previousAnswer = documentVersion.answers.find(
        (a) => a.element === dataKey
      );
      const isDeleteMutation = answer === null;

      const updatedDoc = {
        ...previousDoc,
        versions: previousDoc?.versions.map((version) => {
          if (version.id === documentVersion.id) {
            const remainingAnswers = version.answers.filter(
              (a: DocumentAnswer) => a.element !== dataKey
            );

            return {
              ...version,
              answers: isDeleteMutation
                ? remainingAnswers
                : [
                    ...remainingAnswers,
                    {
                      documentVersionId: documentVersion.id,
                      id: previousAnswer?.id || "temp",
                      element: dataKey,
                      answer: answer,
                      createdBy: previousAnswer?.createdBy || "temp",
                      createdAt: previousAnswer?.createdAt || new Date(),
                      updatedAt: new Date(),
                    },
                  ],
            };
          } else {
            return version;
          }
        }),
      };

      queryClient.setQueryData(getDocumentQueryKey(docId), updatedDoc);

      queryClient.setQueryData(
        getDocumentsQueryKey(deviceId),
        (old: Document[]) => {
          if (!old) return [updatedDoc];
          return [...old.filter((d) => d.id !== docId), updatedDoc];
        }
      );
    },
    onSuccess: (_, { orgId }) => {
      invalidateDynamicContent(queryClient);
      getTaskListAndSummaryInvalidations(queryClient, orgId);
    },
  });
};

export const useSaveAnswerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      answer,
      step,
      documentVersion,
      answerFileId,
    }: {
      orgId: string;
      docId: string;
      deviceId: string;
      step: TemplateQuestion;
      documentVersion: DocumentVersion;
      answerFileId?: string;
      answer?: string;
    }) => {
      const updatedDocVersion = await postDocumentAnswer(
        orgId,
        documentVersion.documentId,
        documentVersion.id,
        {
          answer,
          element: step.id,
          answerFileId,
        }
      );
      return DocumentAnswerSchema.validateSync(updatedDocVersion.data);
    },
    onMutate: async ({
      docId,
      deviceId,
      answer,
      step,
      documentVersion,
      answerFileId,
    }) => {
      // cancel ongoing
      queryClient.cancelQueries({
        queryKey: getDocumentQueryKey(docId),
      });

      queryClient.cancelQueries({
        queryKey: getDocumentsQueryKey(deviceId),
      });

      let previousDocs: Document[] | undefined = queryClient.getQueryData(
        getDocumentsQueryKey(deviceId)
      );

      let previousDoc: Document | undefined =
        queryClient.getQueryData(getDocumentQueryKey(docId)) ||
        previousDocs?.find((d) => d.id === docId);

      if (!previousDoc) {
        return;
      }

      const previousAnswer = documentVersion.answers.find(
        (a) => a.element === step.id
      );

      const newAnswer: DocumentAnswer = {
        documentVersionId: documentVersion.id,
        id: previousAnswer?.id || "temp",
        element: step.id,
        answer: answer,
        answerFileId: answerFileId,
        createdBy: previousAnswer?.createdBy || "temp",
        createdAt: previousAnswer?.createdAt || new Date(),
        updatedAt: new Date(),
      };

      const updatedDoc = {
        ...previousDoc,
        versions: previousDoc?.versions.map((version) => {
          if (version.id === documentVersion.id) {
            return {
              ...version,
              answers: [
                ...version.answers.filter(
                  (a: DocumentAnswer) => a.element !== step.id
                ),
                newAnswer,
              ],
            };
          } else {
            return version;
          }
        }),
      };

      queryClient.setQueryData(getDocumentQueryKey(docId), updatedDoc);

      queryClient.setQueryData(
        getDocumentsQueryKey(deviceId),
        (old: Document[]) => {
          if (!old) return [updatedDoc];
          return [...old.filter((d) => d.id !== docId), updatedDoc];
        }
      );
    },
    onSuccess: (savedAnswer, { orgId, docId, deviceId }) => {
      // Reconcile the optimistic answer (which onMutate wrote with a "temp" id
      // for brand-new answers) with the real server-generated answer. Without
      // this, the cached answer keeps id "temp" until a manual refresh, so a
      // follow-up save (e.g. creating an answer item) posts to
      // .../answer/temp/item and fails backend uuid validation (400).
      const reconcileDoc = (doc: Document): Document => ({
        ...doc,
        versions: doc.versions.map((version) => {
          if (version.id !== savedAnswer.documentVersionId) {
            return version;
          }
          const hasAnswer = version.answers.some(
            (a) => a.element === savedAnswer.element
          );
          return {
            ...version,
            answers: hasAnswer
              ? version.answers.map((a) =>
                  a.element === savedAnswer.element
                    ? {
                        ...savedAnswer,
                        // Preserve already-cached items so re-saving an existing
                        // answer doesn't drop its answer items.
                        answerItems: a.answerItems ?? savedAnswer.answerItems,
                      }
                    : a
                )
              : [...version.answers, savedAnswer],
          };
        }),
      });

      queryClient.setQueryData(getDocumentQueryKey(docId), (old?: Document) =>
        old ? reconcileDoc(old) : old
      );

      queryClient.setQueryData(
        getDocumentsQueryKey(deviceId),
        (old?: Document[]) =>
          old?.map((d) => (d.id === docId ? reconcileDoc(d) : d))
      );

      invalidateDynamicContent(queryClient);
      getTaskListAndSummaryInvalidations(queryClient, orgId);
    },
  });
};

export const useUpdateUserMutation = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, user }: { id: string; user: Partial<User> }) =>
      patchUserInOrg(orgId, id, user),
    onMutate: ({ user }) => {
      queryClient.setQueryData(getUserQueryKey(), (old: User) => ({
        ...old,
        ...user,
      }));
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getOrganizationUsersQueryKey({ orgId }),
      });
      queryClient.invalidateQueries({
        queryKey: getUserQueryKey(),
      });
    },
  });
};

export const useUpdateUserMeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (user: Partial<User>) => patchUserMe(user),
    onMutate: async (user) => {
      await queryClient.cancelQueries({
        queryKey: getUserQueryKey(),
      });

      const previousUser = queryClient.getQueryData<User>(getUserQueryKey());

      queryClient.setQueryData(getUserQueryKey(), (old: User | undefined) => {
        if (!old) {
          return old;
        }

        return {
          ...old,
          ...user,
        };
      });

      return { previousUser };
    },
    onError: (_, __, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData(getUserQueryKey(), context.previousUser);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getUserQueryKey(),
      });
    },
  });
};

export const useUpdateOrgMutation = (orgId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name?: string; companyDescription?: string | null }) =>
      updateOrg(orgId, data),
    onMutate: (data) => {
      queryClient.setQueryData(getUserQueryKey(), (old: User | undefined) => {
        if (!old) {
          return old;
        }

        return {
          ...old,
          organizationMemberships: old.organizationMemberships.map(
            (membership) =>
              membership.organizationId === orgId
                ? {
                    ...membership,
                    organization: {
                      ...membership.organization,
                      ...data,
                    },
                  }
                : membership
          ),
        };
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getOrganizationUsersQueryKey({ orgId }),
      });
      queryClient.invalidateQueries({
        queryKey: getUserQueryKey(),
      });
    },
  });
};

export const useInviteUserMutation = (orgId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      inviteeEmail,
      inviteeFirstName,
      inviteeLastName,
    }: {
      inviteeEmail: string;
      inviteeFirstName: string;
      inviteeLastName: string;
    }) =>
      postUserInvite(orgId, {
        inviteeEmail,
        inviteeFirstName,
        inviteeLastName,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getUserQueryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: getOrganizationUsersQueryKey({ orgId }),
      });
    },
  });
};

export const usePatchApproverStatusMutation = ({
  orgId,
  documentId,
  versionId,
}: {
  orgId: string;
  documentId: string;
  versionId: string;
  deviceId: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: getPatchApproverStatusMutationKey(),
    mutationFn: (data: {
      approverId: string;
      status: "USER_INVITE_SENT" | "AWAITING_SIGNATURE" | "APPROVED";
    }) =>
      patchApproverStatus({
        orgId,
        documentId,
        versionId,
        status: data.status,
      }),
    onMutate: async (data) => {
      const nextStatus = data.status as DocumentVersionApproverStatus;

      await queryClient.cancelQueries({
        queryKey: getApproversQueryKey(documentId, versionId),
      });
      await queryClient.cancelQueries({
        queryKey: getDocumentQueryKey(documentId),
      });
      await queryClient.cancelQueries({
        queryKey: baseDocumentsQueryKey,
      });

      const previousData: DocumentVersionApprover[] | undefined =
        queryClient.getQueryData(getApproversQueryKey(documentId, versionId));
      const previousDocument: Document | undefined = queryClient.getQueryData(
        getDocumentQueryKey(documentId)
      );
      const previousDocumentLists = queryClient
        .getQueryCache()
        .findAll({
          queryKey: baseDocumentsQueryKey,
        })
        .map((query): [QueryKey, Document[] | undefined] => [
          query.queryKey,
          queryClient.getQueryData<Document[]>(query.queryKey),
        ]);

      if (previousData) {
        const nextData = patchApproverStatusInApprovers(
          previousData,
          data.approverId,
          nextStatus
        );

        // We update the local cache to reflect the new status
        queryClient.setQueryData(
          getApproversQueryKey(documentId, versionId),
          nextData
        );
      }

      if (previousDocument) {
        queryClient.setQueryData(
          getDocumentQueryKey(documentId),
          patchApproverStatusInDocument({
            document: previousDocument,
            documentId,
            versionId,
            userId: data.approverId,
            status: nextStatus,
          })
        );
      }

      previousDocumentLists.forEach(([queryKey, documents]) => {
        if (!documents) {
          return;
        }

        queryClient.setQueryData(
          queryKey,
          documents.map((document) =>
            patchApproverStatusInDocument({
              document,
              documentId,
              versionId,
              userId: data.approverId,
              status: nextStatus,
            })
          )
        );
      });

      return { previousData, previousDocument, previousDocumentLists };
    },
    onError: (_, __, context) => {
      // If the mutation fails, we revert the local cache to the previous state
      if (context?.previousData) {
        queryClient.setQueryData(
          getApproversQueryKey(documentId, versionId),
          context.previousData
        );
      }
      if (context?.previousDocument) {
        queryClient.setQueryData(
          getDocumentQueryKey(documentId),
          context.previousDocument
        );
      }
      context?.previousDocumentLists.forEach(([queryKey, documents]) => {
        queryClient.setQueryData(queryKey, documents);
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getApproversQueryKey(documentId, versionId),
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentQueryKey(documentId),
      });
      queryClient.invalidateQueries({
        queryKey: baseDocumentsQueryKey,
      });
      getTaskListAndSummaryInvalidations(queryClient, orgId);
    },
  });
};

export const useReplaceApproversMutation = ({
  orgId,
  documentId,
  versionId,
  deviceId,
}: {
  orgId: string;
  documentId: string;
  versionId: string;
  deviceId: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (users: BaseUser[]) =>
      replaceDocumentApprovers({
        orgId,
        documentId,
        versionId,
        userIds: users.map((user) => user.id),
      }),
    onMutate: async (users) => {
      const key = getApproversQueryKey(documentId, versionId);
      await queryClient.cancelQueries({ queryKey: key });
      const previousData =
        queryClient.getQueryData<DocumentVersionApprover[]>(key);
      queryClient.setQueryData(
        key,
        users.map(
          (user) =>
            previousData?.find((row) => row.userId === user.id) ?? {
              id: uniqueId(),
              user,
              userId: user.id,
              documentVersionId: versionId,
              createdAt: new Date(),
              updatedAt: new Date(),
              approvalStatus: user.authId
                ? DocumentVersionApproverStatus.AWAITING_SIGNATURE
                : DocumentVersionApproverStatus.USER_INVITE_SENT,
            }
        )
      );
      return { previousData };
    },
    onError: (_error, _users, context) => {
      queryClient.setQueryData(
        getApproversQueryKey(documentId, versionId),
        context?.previousData
      );
    },
    onSuccess: ({ data }) => {
      queryClient.setQueryData(
        getApproversQueryKey(documentId, versionId),
        data
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getApproversQueryKey(documentId, versionId),
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentQueryKey(documentId),
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentsQueryKey(deviceId),
      });
      getTaskListAndSummaryInvalidations(queryClient, orgId);
    },
  });
};

export const usePostApproverMutation = ({
  orgId,
  documentId,
  versionId,
  approvers,
}: {
  orgId: string;
  documentId: string;
  versionId: string;
  deviceId: string;
  approvers: (BaseUser | null)[];
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { userId: string }) =>
      postDocumentApprover({
        orgId,
        documentId,
        versionId,
        data,
      }),
    onMutate: async (data) => {
      await queryClient.cancelQueries({
        queryKey: getApproversQueryKey(documentId, versionId),
      });

      const previousData: DocumentVersionApprover[] | undefined =
        queryClient.getQueryData(getApproversQueryKey(documentId, versionId));
      const user = approvers.find((user) => user?.id === data.userId);

      if (user) {
        const newApprover: DocumentVersionApprover = {
          id: uniqueId(),
          user: user,
          createdAt: new Date(),
          updatedAt: new Date(),
          documentVersionId: versionId,
          userId: data.userId,
          approvalStatus: user?.authId
            ? DocumentVersionApproverStatus.AWAITING_SIGNATURE
            : DocumentVersionApproverStatus.USER_INVITE_SENT,
        };

        const nextData = previousData?.some(
          (approver) => approver.userId === data.userId
        )
          ? previousData
          : [...(previousData || []), newApprover];

        queryClient.setQueryData(
          getApproversQueryKey(documentId, versionId),
          nextData
        );
      }

      return { previousData };
    },

    onError: (_error, _data, context) => {
      queryClient.setQueryData(
        getApproversQueryKey(documentId, versionId),
        context?.previousData
      );
    },

    onSuccess: () => {
      // Only invalidate the approvers query since we're handling the optimistic update
      queryClient.invalidateQueries({
        queryKey: getApproversQueryKey(documentId, versionId),
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentQueryKey(documentId),
      });
      getTaskListAndSummaryInvalidations(queryClient, orgId);
    },
  });
};

export const useDeleteApproverMutation = ({
  orgId,
  documentId,
  versionId,
  deviceId,
}: {
  orgId: string;
  documentId: string;
  versionId: string;
  deviceId: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { approverId: string }) =>
      deleteDocumentApprover(orgId, documentId, versionId, data.approverId),
    onMutate: async (data) => {
      await queryClient.cancelQueries({
        queryKey: getApproversQueryKey(documentId, versionId),
      });

      const previousData: DocumentVersionApprover[] | undefined =
        queryClient.getQueryData(getApproversQueryKey(documentId, versionId));

      if (previousData) {
        queryClient.setQueryData(
          getApproversQueryKey(documentId, versionId),
          previousData.filter((approver) => approver.id !== data.approverId)
        );
      }

      return { previousData };
    },
    onError: (_error, _data, context) => {
      queryClient.setQueryData(
        getApproversQueryKey(documentId, versionId),
        context?.previousData
      );
    },
    onSuccess: () => {
      // Only invalidate the approvers query since we're handling the optimistic update
      queryClient.invalidateQueries({
        queryKey: getApproversQueryKey(documentId, versionId),
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentsQueryKey(deviceId),
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentQueryKey(documentId),
      });
      getTaskListAndSummaryInvalidations(queryClient, orgId);
    },
  });
};

export const usePatchDocumentVersionDataKeysMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      docId,
      docVersionId,
      dataKeyKeys,
    }: {
      orgId: string;
      deviceId: string;
      docId: string;
      docVersionId: string;
      dataKeyKeys: string[];
    }) => {
      const { data } = await patchDocumentVersionDataKeys(
        orgId,
        docId,
        docVersionId,
        { dataKeyKeys }
      );
      return DocumentVersionSchema.validateSync(data);
    },
    onSuccess: (_updatedVersion, { docId }) => {
      queryClient.invalidateQueries({
        queryKey: baseDocumentsQueryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentQueryKey(docId),
      });
    },
  });
};

export const useCreateDocumentVersionMutation = (
  orgId: string,
  deviceId: string
) => {
  const queryClient = useQueryClient();

  const getPreviewReadyVersion = ({
    version,
    uploadedFile,
  }: {
    version: DocumentVersion;
    uploadedFile?: File;
  }): DocumentVersion => {
    if (!uploadedFile || !version.fileId) {
      return version;
    }

    return {
      ...version,
      file: {
        ...version.file,
        id: version.file?.id || version.fileId || undefined,
        filename: version.file?.filename || uploadedFile.name || undefined,
        mimeType: version.file?.mimeType || uploadedFile.type || undefined,
      },
    };
  };

  return useMutation({
    mutationFn: async ({
      docId,
      file,
      extraData,
    }: {
      docId: string;
      file?: File;
      extraData?: {
        contentTemplate?: string;
        readyForApproval?: boolean;
      };
    }) => {
      const { data } = await createDocumentVersion(orgId, docId, {
        file: file && removeSpecialCharactersFomFilename(file),
        contentTemplate: extraData?.contentTemplate,
        readyForApproval: extraData?.readyForApproval,
      });
      return DocumentVersionSchema.validateSync(data);
    },
    onSuccess: async (data, { docId, file }) => {
      const previewReadyVersion = getPreviewReadyVersion({
        version: data,
        uploadedFile: file,
      });

      // Seed the file blob cache so useGetFile returns immediately
      // after creating a version with a file upload
      if (file && data.fileId) {
        queryClient.setQueryData(getFileQueryKey(data.fileId), {
          data: file,
        });
      }

      // get the previous data. Sometimes the document alone might not be in the cache since we are triggering this from a page where the getDocument query is not run.
      const currentDocFromAllDocs = (
        queryClient.getQueryData(getDocumentsQueryKey(deviceId)) as
          | Document[]
          | undefined
      )?.find((doc) => doc.id === docId);

      // Update cache for both file and non-file uploads.
      // The backend's createDocumentVersion always calls deleteDraftVersions,
      // which removes all versions with revision === null (except the new one).
      // Mirror that here so the cache stays consistent.
      const filterVersions = (versions: DocumentVersion[]) =>
        orderBy(
          [previewReadyVersion, ...versions.filter((v) => v.revision !== null)],
          "createdAt",
          "desc"
        );

      queryClient.setQueryData(
        getDocumentQueryKey(docId),
        (old: Document | undefined) => {
          const previousData = old || currentDocFromAllDocs;
          if (previousData) {
            return {
              ...previousData,
              versions: filterVersions(previousData.versions || []),
            };
          } else {
            return old;
          }
        }
      );
      queryClient.setQueryData(
        getDocumentsQueryKey(deviceId),
        (old: Document[] | undefined) => {
          return old?.map((doc) => {
            if (doc.id === docId) {
              return {
                ...doc,
                versions: filterVersions(doc.versions),
              };
            } else {
              return doc;
            }
          });
        }
      );

      await Promise.all([
        ...getTaskListAndSummaryInvalidations(queryClient, orgId),
        queryClient.invalidateQueries({
          queryKey: getDocumentQueryKey(docId),
        }),
        queryClient.invalidateQueries({
          queryKey: getDocumentsQueryKey(deviceId),
        }),
        data.fileId &&
          !file &&
          queryClient.invalidateQueries({
            queryKey: getFileQueryKey(data.fileId),
          }),
      ]);
    },
  });
};

export const useCheckoutMutation = () => {
  return useMutation({
    mutationFn: async ({
      orgId,
      plan,
      amount,
      cycle,
    }: {
      orgId: string;
      plan: Plan.PREMIUM | Plan.PREMIUM_PLUS;
      amount: number;
      cycle: BillingInterval;
    }) => {
      const { data } = await checkout(orgId, plan, amount, cycle);
      return data as string;
    },
  });
};

export const useCreateBillingSessionMutation = () => {
  return useMutation({
    mutationFn: async (orgId: string) => {
      const { data } = await postBillingSession(orgId);
      return data as string;
    },
  });
};

export const useDeleteFileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      fileId,
    }: {
      orgId: string;
      fileId: string;
    }) => {
      return await deleteFile({ orgId, fileId });
    },
    onSuccess: (_, { fileId }) => {
      // Invalidate the queries for all files in org
      queryClient.invalidateQueries({
        queryKey: getAllFilesInOrgQueryKey(),
      });

      // Remove the file from the file query cache
      queryClient.removeQueries({
        queryKey: getFileQueryKey(fileId),
      });

      queryClient.invalidateQueries({
        queryKey: getFileQueryKey(fileId),
      });

      queryClient.invalidateQueries({
        queryKey: baseDocumentsQueryKey,
      });

      queryClient.invalidateQueries({
        queryKey: baseDocumentQueryKey,
      });
    },
  });
};

export const useUpdateUserUiStateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdatableUiState) => {
      const { data } = await updateUserUiState(body);
      return FullUserUiStateSchema.validateSync(data);
    },
    onMutate: async (body: UpdatableUiState) => {
      // await queryClient.cancelQueries({ queryKey: getUserQueryKey() });

      const previousData: User | undefined =
        queryClient.getQueryData(getUserQueryKey());

      if (previousData) {
        queryClient.setQueryData(getUserQueryKey(), {
          ...previousData,
          userUiState: { ...previousData.userUiState, ...body },
        });
      }
    },
    // Not reverting state onError because we don't want continual popups if the mutation fails
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getUserQueryKey() });
    },
  });
};

export const useCreateEcfrPinMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (pin: string) => {
      const { data } = await createEcfrPin(pin);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getUserQueryKey(),
      });
    },
  });
};

export const useUpdateEcfrPinMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (pin: string) => {
      const { data } = await updateEcfrPin(pin);

      //the be returns an empty string,
      // to validate it with yup we normalize it to an empty object
      const result = data || {};

      return object({}).validateSync(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getUserQueryKey(),
      });
    },
  });
};

export const useValidateEcfrPinMutation = () => {
  return useMutation({
    mutationFn: async (pin: string) => {
      const { data } = await validateEcfrPin(pin);
      return data;
    },
  });
};

export const useSendPinResetEmailMutation = () => {
  return useMutation({
    mutationFn: async () => {
      await sendPinResetEmail();
    },
  });
};

export const useDeleteDocumentDraftsMutation = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: async ({
      orgId,
      docId,
    }: {
      orgId: string;
      deviceId: string;
      docId: string;
      versionId: string;
    }) => {
      await deleteDocumentDrafts(orgId, docId);
    },
    onMutate: async ({ deviceId, docId }) => {
      return applyOptimisticDeleteVersionUpdate({
        queryClient,
        deviceId,
        docId,
        getRemainingVersions: removeDraftDocumentVersions,
      });
    },
    onError: (error, { deviceId, docId }, context) => {
      if (context?.previousDocument) {
        queryClient.setQueryData(
          getDocumentQueryKey(docId),
          context.previousDocument
        );
      }

      if (context?.previousDocuments) {
        queryClient.setQueryData(
          getDocumentsQueryKey(deviceId),
          context.previousDocuments
        );
      }

      showSnackbar(
        getApiErrorMessage(
          error,
          "Could not delete document drafts because the version owns a data key used by an other approved document version."
        )
      );
    },
    onSuccess: (_, { deviceId, docId, orgId }, context) => {
      handleDeleteVersionMutationSuccess({
        queryClient,
        orgId,
        deviceId,
        docId,
        context,
      });
    },
  });
};

export const useDeleteDocumentMutation = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: async ({
      orgId,
      docId,
    }: {
      orgId: string;
      deviceId: string;
      docId: string;
      queryDeviceIds?: string[];
      suppressErrorSnackbar?: boolean;
    }) => {
      await deleteDocument(orgId, docId);
    },
    onMutate: async ({ deviceId, docId, queryDeviceIds }) => {
      const previousDocumentsByDeviceId = new Map<string, Document[]>();
      const nextQueryDeviceIds = [
        ...new Set([...(queryDeviceIds ?? []), deviceId].filter(Boolean)),
      ];

      nextQueryDeviceIds.forEach((queryDeviceId) => {
        const previousDocuments = queryClient.getQueryData<Document[]>(
          getDocumentsQueryKey(queryDeviceId)
        );

        if (!previousDocuments) {
          return;
        }

        previousDocumentsByDeviceId.set(queryDeviceId, previousDocuments);
        queryClient.setQueryData(
          getDocumentsQueryKey(queryDeviceId),
          (old: Document[]) => old.filter((d) => d.id !== docId)
        );
      });

      return { previousDocumentsByDeviceId };
    },
    onError: (error, { suppressErrorSnackbar }, context) => {
      context?.previousDocumentsByDeviceId?.forEach(
        (previousDocuments, queryDeviceId) => {
          queryClient.setQueryData(
            getDocumentsQueryKey(queryDeviceId),
            previousDocuments
          );
        }
      );

      if (suppressErrorSnackbar) {
        return;
      }

      showSnackbar(
        getApiErrorMessage(
          error,
          "Could not delete document because the version owns a data key used by an other approved document version."
        )
      );
    },
    onSuccess: (_, { orgId }) => {
      invalidateDocumentLists(queryClient, orgId);
      getTaskListAndSummaryInvalidations(queryClient, orgId);
    },
  });
};

export const useRestoreDocumentMutation = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ orgId, docId }: { orgId: string; docId: string }) => {
      await restoreDocument(orgId, docId);
    },
    onError: (error) => {
      showSnackbar(getApiErrorMessage(error, "Could not restore document."));
    },
    onSuccess: (_, { docId, orgId }) => {
      queryClient.invalidateQueries({
        queryKey: getDocumentQueryKey(docId),
      });
      invalidateDocumentLists(queryClient, orgId);
      getTaskListAndSummaryInvalidations(queryClient, orgId);
    },
  });
};

export const usePermanentlyDeleteDocumentMutation = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ orgId, docId }: { orgId: string; docId: string }) => {
      await permanentlyDeleteDocument(orgId, docId);
    },
    onError: (error) => {
      showSnackbar(
        getApiErrorMessage(error, "Could not permanently delete document.")
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: baseDocumentTrashQueryKey,
        cancelRefetch: false,
      });
    },
  });
};

export const useEmptyDocumentTrashMutation = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ orgId }: { orgId: string }) => {
      const { data } = await emptyDocumentTrash(orgId);
      return data;
    },
    onError: (error) => {
      showSnackbar(getApiErrorMessage(error, "Could not empty Trash."));
    },
    onSuccess: (summary) => {
      if (summary.failed > 0) {
        showSnackbar(
          `${summary.failed} document${summary.failed === 1 ? "" : "s"} could not be permanently deleted.`
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: baseDocumentTrashQueryKey,
        cancelRefetch: false,
      });
    },
  });
};

export const useDeleteDocumentVersionMutation = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: async ({
      orgId,
      docId,
      versionId,
    }: {
      orgId: string;
      deviceId: string;
      docId: string;
      versionId: string;
    }) => {
      await deleteDocumentVersion(orgId, docId, versionId);
    },
    onMutate: async ({ deviceId, docId, versionId }) => {
      return applyOptimisticDeleteVersionUpdate({
        queryClient,
        deviceId,
        docId,
        getRemainingVersions: (versions) =>
          removeDocumentVersionById(versions, versionId),
      });
    },
    onError: (error, { deviceId, docId }, context) => {
      if (context?.previousDocument) {
        queryClient.setQueryData(
          getDocumentQueryKey(docId),
          context.previousDocument
        );
      }

      if (context?.previousDocuments) {
        queryClient.setQueryData(
          getDocumentsQueryKey(deviceId),
          context.previousDocuments
        );
      }

      showSnackbar(
        getApiErrorMessage(
          error,
          "Could not delete document version because the version owns a data key used by an other approved document version."
        )
      );
    },
    onSuccess: (_, { deviceId, docId, orgId }, context) => {
      handleDeleteVersionMutationSuccess({
        queryClient,
        orgId,
        deviceId,
        docId,
        context,
      });
    },
  });
};

export const useParseWebsiteToDeviceMutation = () => {
  return useMutation({
    mutationFn: async ({
      url,
    }: {
      url: string;
    }): Promise<CreateDevice | { error: string }> => {
      try {
        const { data } = await parseWebsiteToDevice(url);

        return createDeviceResponseSchema.validateSync(data).result;
      } catch (error: any) {
        return {
          error: error?.response?.data?.detail || error?.message,
        };
      }
    },
  });
};

export const useDeleteDeviceMutation = () => {
  const queryClient = useQueryClient();
  const { deviceStore } = useStore();
  return useMutation({
    mutationFn: ({ orgId, id }: { orgId: string; id: string }) =>
      deleteDevice(orgId, id),
    onMutate: ({ orgId, id }) => {
      const previousDevices: Device[] | undefined = queryClient.getQueryData(
        getDevicesQueryKey(orgId)
      );
      const previousActiveGlobalDeviceId =
        deviceStore.getActiveGlobalDeviceId(orgId);
      const remainingDevices =
        previousDevices?.filter((device) => device.id !== id) ?? [];
      const nextActiveGlobalDeviceId =
        previousActiveGlobalDeviceId === id
          ? ([...remainingDevices].sort((left, right) =>
              left.name.localeCompare(right.name)
            )[0]?.id ?? null)
          : previousActiveGlobalDeviceId;

      queryClient.setQueryData(
        getDevicesQueryKey(orgId),
        (old: Device[] | undefined) => {
          return old?.filter((device) => device.id !== id);
        }
      );

      if (previousActiveGlobalDeviceId === id) {
        deviceStore.setActiveGlobalDeviceId(orgId, nextActiveGlobalDeviceId);
      }

      return { previousActiveGlobalDeviceId, previousDevices };
    },
    onError: (_, { orgId }, context) => {
      if (context?.previousDevices) {
        queryClient.setQueryData(
          getDevicesQueryKey(orgId),
          context.previousDevices
        );
      }

      if (context?.previousActiveGlobalDeviceId !== undefined) {
        deviceStore.setActiveGlobalDeviceId(
          orgId,
          context.previousActiveGlobalDeviceId
        );
      }
    },
    onSettled: () => {
      invalidateCachedDashboardQueries(queryClient);
    },
  });
};

const useOrganizationMembershipActivationMutation = (
  orgId: string,
  deviceId: string,
  activated: boolean,
  updateMembership: (orgId: string, userId: string) => Promise<unknown>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      await updateMembership(orgId, userId);
    },
    onMutate: async ({ userId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: getOrganizationUsersQueryKey({ orgId }),
      });

      const previousUsers = queryClient.getQueryData(
        getOrganizationUsersQueryKey({ orgId })
      );

      queryClient.setQueryData(
        getOrganizationUsersQueryKey({ orgId }),
        (old: OrgScopedBaseUser[] | undefined) => {
          return old?.map((user) =>
            user.id === userId
              ? {
                  ...user,
                  organizationMemberships: user.organizationMemberships?.map(
                    (membership) =>
                      membership.organizationId === orgId
                        ? { ...membership, activated }
                        : membership
                  ),
                }
              : user
          );
        }
      );

      return { previousUsers };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(
        getOrganizationUsersQueryKey({ orgId }),
        context?.previousUsers
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getOrganizationUsersQueryKey({ orgId }),
      });
      queryClient.invalidateQueries({
        queryKey: getUserTodosQueryKey(orgId, deviceId),
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentsQueryKey(deviceId),
      });
    },
  });
};

export const useDeleteUserFromOrgMutation = (orgId: string, deviceId: string) =>
  useOrganizationMembershipActivationMutation(
    orgId,
    deviceId,
    false,
    deleteUserFromOrg
  );

export const useReactivateUserInOrgMutation = (
  orgId: string,
  deviceId: string
) =>
  useOrganizationMembershipActivationMutation(
    orgId,
    deviceId,
    true,
    reactivateUserInOrg
  );

export const useCreateCustomTemplateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      deviceId,
      data,
    }: {
      orgId: string;
      deviceId: string;
      data: {
        templateKey: string;
        defaultTitle: string;
        // functionalGroupId: string;
      };
    }) => {
      const { data: template } = await postCustomTemplate(
        orgId,
        deviceId,
        data
      );
      const templateValidated = await documentTemplateSchema.validate(template);
      return transformTemplateToFormat(templateValidated);
    },
    onMutate: ({ orgId, deviceId, data }) => {
      const previousTemplates: TemplateConfig | undefined =
        queryClient.getQueryData(getDocumentTemplatesQueryKey(orgId, deviceId));

      queryClient.setQueryData(
        getDocumentTemplatesQueryKey(orgId, deviceId),
        (old: TemplateConfig | undefined) => {
          return {
            ...old,
            [data.templateKey]: {
              ...data,
              questions: [],
            },
          };
        }
      );

      return { previousTemplates };
    },
    onSuccess: (_, { orgId, deviceId }) => {
      queryClient.invalidateQueries({
        queryKey: getDocumentTemplatesQueryKey(orgId, deviceId),
      });
    },
  });
};

export const usePatchCustomTemplateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      deviceId,
      templateKey,
      data,
    }: {
      orgId: string;
      deviceId: string;
      templateKey: string;
      data: {
        defaultTitle?: string;
        functionalGroupAcronym?: string | null;
        regulatoryType?: RegulatoryType;
        contentTemplate: string;
      };
    }) => {
      const { data: template } = await patchCustomTemplate(
        orgId,
        deviceId,
        templateKey,
        data
      );
      const templateValidated = await documentTemplateSchema.validate(template);
      return transformTemplateToFormat(templateValidated);
    },
    onMutate: async ({ orgId, deviceId, templateKey, data }) => {
      const previousTemplates: TemplateConfig | undefined =
        queryClient.getQueryData(getDocumentTemplatesQueryKey(orgId, deviceId));
      const previousTemplate = previousTemplates?.[templateKey];
      // Optimistically update the cache
      queryClient.setQueryData(
        getDocumentTemplatesQueryKey(orgId, deviceId),
        (old: TemplateConfig | undefined) => {
          return {
            ...old,
            [templateKey]: {
              ...previousTemplate,
              ...data,
              questions: [],
            },
          };
        }
      );

      return { previousTemplates };
    },
    onError: (_err, variables, context) => {
      // Rollback on error
      if (context?.previousTemplates) {
        queryClient.setQueryData(
          getDocumentTemplatesQueryKey(variables.orgId, variables.deviceId),
          context.previousTemplates
        );
      }
    },
    onSuccess: (updatedTemplate, { orgId, deviceId, templateKey }) => {
      // Merge the returned template into the cache
      queryClient.setQueryData(
        getDocumentTemplatesQueryKey(orgId, deviceId),
        (old: TemplateConfig | undefined) => {
          return {
            ...old,
            [templateKey]: updatedTemplate,
          };
        }
      );
      // No need to invalidate unless you want to force a refetch
    },
  });
};

export const useUpdateThreadMutation = () => {
  const { agentStore } = useStore();
  return useMutation({
    mutationFn: async ({
      threadId,
      metadata,
    }: {
      threadId: string;
      metadata: { name: string };
    }) => {
      await agentStore.updateThread(threadId, metadata);
    },
  });
};

export const useDisconnectIntegration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, type }: { orgId: string; type: IntegrationType }) =>
      disconnectIntegration(orgId, type),
    onSuccess: (_, { orgId, type }) => {
      queryClient.invalidateQueries({
        queryKey: getIntegrationQueryKey(orgId, type),
      });
    },
  });
};

export const useSetIntegrationEnabledMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      type,
      enabled,
    }: {
      orgId: string;
      type: IntegrationType;
      enabled: boolean;
    }) => {
      const { data } = await setIntegrationEnabled(orgId, type, enabled);
      return IntegrationEnabledResponseSchema.validateSync(data);
    },
    onMutate: async ({ orgId, type, enabled }) => {
      const queryKey = getIntegrationQueryKey(orgId, type);

      await queryClient.cancelQueries({ queryKey });
      const previousIntegration = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.integration) {
          return old;
        }
        return {
          ...old,
          integration: {
            ...old.integration,
            enabled,
          },
        };
      });

      return { previousIntegration };
    },
    onError: (_error, { orgId, type }, context) => {
      if (context?.previousIntegration !== undefined) {
        queryClient.setQueryData(
          getIntegrationQueryKey(orgId, type),
          context.previousIntegration
        );
      }
    },
    onSettled: (_, __, { orgId, type }) => {
      queryClient.invalidateQueries({
        queryKey: getIntegrationQueryKey(orgId, type),
      });
    },
  });
};

export const useCreateManualMcpConnectionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      name,
      serverUrl,
      accessToken,
    }: {
      orgId: string;
      name: string;
      serverUrl: string;
      accessToken: string;
    }) => {
      const { data } = await createManualMcpConnection(orgId, {
        name,
        serverUrl,
        accessToken,
      });
      return McpConnectionResponseSchema.validateSync(data);
    },
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: getMcpConnectionsQueryKey(orgId),
      });
    },
  });
};

export const useCreateAuthlessMcpConnectionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      name,
      serverUrl,
      presetKey,
    }: {
      orgId: string;
      name: string;
      serverUrl: string;
      presetKey?: McpConnectionPresetKey;
    }) => {
      const { data } = await createAuthlessMcpConnection(orgId, {
        name,
        serverUrl,
        presetKey,
      });
      return McpConnectionResponseSchema.validateSync(data);
    },
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: getMcpConnectionsQueryKey(orgId),
      });
    },
  });
};

export const useStartMcpOAuthConnectionMutation = () => {
  return useMutation({
    mutationFn: async ({
      orgId,
      name,
      serverUrl,
      presetKey,
    }: {
      orgId: string;
      name: string;
      serverUrl: string;
      presetKey?: McpConnectionPresetKey;
    }) => {
      const { data } = await startMcpOAuthConnection(orgId, {
        name,
        serverUrl,
        presetKey,
      });
      return StartMcpOAuthResponseSchema.validateSync(data);
    },
  });
};

export const useDisconnectMcpConnectionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      connectionId,
    }: {
      orgId: string;
      connectionId: string;
    }) => {
      const { data } = await disconnectMcpConnection(orgId, connectionId);
      return McpConnectionActionResponseSchema.validateSync(data);
    },
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: getMcpConnectionsQueryKey(orgId),
      });
    },
  });
};

export const useTestMcpConnectionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      connectionId,
    }: {
      orgId: string;
      connectionId: string;
    }) => {
      const { data } = await testMcpConnection(orgId, connectionId);
      return McpConnectionActionResponseSchema.validateSync(data);
    },
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: getMcpConnectionsQueryKey(orgId),
      });
    },
  });
};

export const useSetMcpConnectionEnabledMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      connectionId,
      enabled,
    }: {
      orgId: string;
      connectionId: string;
      enabled: boolean;
    }) => {
      const { data } = await setMcpConnectionEnabled(
        orgId,
        connectionId,
        enabled
      );
      return McpConnectionEnabledResponseSchema.validateSync(data);
    },
    onMutate: async ({ orgId, connectionId, enabled }) => {
      const queryKey = getMcpConnectionsQueryKey(orgId);

      await queryClient.cancelQueries({ queryKey });
      const previousConnections = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.connections || !Array.isArray(old.connections)) {
          return old;
        }

        return {
          ...old,
          connections: old.connections.map((connection: any) =>
            connection.id === connectionId
              ? { ...connection, enabled }
              : connection
          ),
        };
      });

      return { previousConnections };
    },
    onError: (_error, { orgId }, context) => {
      if (context?.previousConnections !== undefined) {
        queryClient.setQueryData(
          getMcpConnectionsQueryKey(orgId),
          context.previousConnections
        );
      }
    },
    onSettled: (_, __, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: getMcpConnectionsQueryKey(orgId),
      });
    },
  });
};

export const useSendSlackTestMutation = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ message }: { message?: string } = {}) =>
      sendSlackTest(orgId, message),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getSlackAuthUrlQueryKey(orgId),
      });
    },
  });
};

export const useCreateFunctionalGroupMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      data,
    }: {
      orgId: string;
      data: CreateFunctionalGroupData;
    }) => {
      const { data: result } = await createFunctionalGroup(orgId, data);
      return result;
    },
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: getFunctionalGroupsQueryKey(orgId),
      });
      queryClient.invalidateQueries({
        queryKey: getOrganizationUsersQueryKey({ orgId }),
      });
      queryClient.invalidateQueries({
        queryKey: getUserQueryKey(),
      });
    },
  });
};

export const useUpdateFunctionalGroupMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      groupId,
      data,
    }: {
      orgId: string;
      groupId: string;
      data: PatchFunctionalGroupData;
    }) => {
      const { data: result } = await patchFunctionalGroup(orgId, groupId, data);
      return result;
    },
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: getFunctionalGroupsQueryKey(orgId),
      });
      queryClient.invalidateQueries({
        queryKey: getOrganizationUsersQueryKey({ orgId }),
      });
      queryClient.invalidateQueries({
        queryKey: getUserQueryKey(),
      });
    },
  });
};

export const useDeleteFunctionalGroupMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      groupId,
    }: {
      orgId: string;
      groupId: string;
    }) => {
      const { data: result } = await deleteFunctionalGroup(orgId, groupId);
      return result;
    },
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: getFunctionalGroupsQueryKey(orgId),
      });
      queryClient.invalidateQueries({
        queryKey: getOrganizationUsersQueryKey({ orgId }),
      });
      queryClient.invalidateQueries({
        queryKey: getUserQueryKey(),
      });
    },
  });
};

export const useAddFunctionalGroupMemberMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      groupId,
      organizationMembershipId,
    }: {
      orgId: string;
      groupId: string;
      organizationMembershipId: string;
    }) => {
      const { data: result } = await addFunctionalGroupMember(orgId, groupId, {
        organizationMembershipId,
      });
      return result;
    },
    onSuccess: (_, { orgId }) => {
      getTaskListAndSummaryInvalidations(queryClient, orgId);
      queryClient.invalidateQueries({
        queryKey: getFunctionalGroupsQueryKey(orgId),
      });
      queryClient.invalidateQueries({
        queryKey: getOrganizationUsersQueryKey({ orgId }),
      });
      queryClient.invalidateQueries({
        queryKey: getUserQueryKey(),
      });
    },
  });
};

export const useRemoveFunctionalGroupMemberMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      groupId,
      membershipId,
    }: {
      orgId: string;
      groupId: string;
      membershipId: string;
    }) => {
      const { data: result } = await removeFunctionalGroupMember(
        orgId,
        groupId,
        membershipId
      );
      return result;
    },
    onSuccess: (_, { orgId }) => {
      getTaskListAndSummaryInvalidations(queryClient, orgId);
      queryClient.invalidateQueries({
        queryKey: getFunctionalGroupsQueryKey(orgId),
      });
      queryClient.invalidateQueries({
        queryKey: getOrganizationUsersQueryKey({ orgId }),
      });
      queryClient.invalidateQueries({
        queryKey: getUserQueryKey(),
      });
    },
  });
};
